<script setup>
import { computed, ref, watch } from "vue";
import { useNoteTypeStore } from "@/stores/useNoteTypeStore";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import { toTitleCase } from "@/utils/stringUtils";
import UserCardModal from "@/components/User/UserCardModal.vue";

const props = defineProps({
  show: { type: Boolean, required: true },
  noteTypeId: { type: Number, required: true },
});

const emit = defineEmits(["close"]);

const noteTypeStore = useNoteTypeStore();
const isLoadingData = ref(true);

// Estados para o Modal de Usuário
const showUserModal = ref(false);
const selectedUserId = ref(null);

function openUserModal(userId) {
  if (!userId) return;
  selectedUserId.value = userId;
  showUserModal.value = true;
}

function closeUserModal() {
  showUserModal.value = false;
}

const transparentBackgroundStyle = computed(() => {
  const isDark = document.documentElement.classList.contains("dark");
  const color = isDark ? "#555" : "#ccc";
  return {
    backgroundImage: `
        linear-gradient(45deg, ${color} 12.5%, transparent 12.5%, transparent 87.5%, ${color} 87.5%),
        linear-gradient(45deg, ${color} 12.5%, transparent 12.5%, transparent 87.5%, ${color} 87.5%)
      `,
    backgroundSize: "20px 20px",
    backgroundPosition: "0 0, 10px 10px",
    backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
    opacity: "0.85",
  };
});

const noteType = computed(() => {
  return (
    noteTypeStore.noteTypes.find((n) => n.noteTypeId === props.noteTypeId) ||
    noteTypeStore.selectedNoteType ||
    null
  );
});

watch(
  () => props.noteTypeId,
  async (newId) => {
    if (!newId) return;
    isLoadingData.value = true;
    try {
      await noteTypeStore.getNoteTypeById(newId);
    } catch (e) {
      console.error("Erro ao carregar tipo de nota no CardModal:", e);
    } finally {
      isLoadingData.value = false;
    }
  },
  { immediate: true }
);

function normalizeAvatarPath(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return path.startsWith("/") ? path : `/${path}`;
}

function getCreatorAvatarUrl(data) {
  if (!data) return "";
  let path = data.createdByUserPhotoPath || data.createdByUser?.photoPath || "";
  if (!path) {
    const userSeed = data.createdByUserId || "default-user";
    return `https://i.pravatar.cc/32?u=${userSeed}`;
  }
  return normalizeAvatarPath(path);
}

function getUpdaterAvatarUrl(data) {
  if (!data) return "";
  let path = data.updatedByUserPhotoPath || data.updatedByUser?.photoPath || "";
  if (!path) {
    const userSeed = data.updatedByUserId || "default-updater";
    return `https://i.pravatar.cc/32?u=${userSeed}`;
  }
  return normalizeAvatarPath(path);
}

const singleWordPreviewStyle = computed(() => {
  if (!noteType.value) return {};
  const nt = noteType.value;
  const isTransparent =
    nt.defaultBackground === "transparent" || !nt.defaultRequiredBackground;

  let style = isTransparent
    ? { ...transparentBackgroundStyle.value, color: nt.defaultColor }
    : { backgroundColor: nt.defaultBackground, color: nt.defaultColor };

  return {
    ...style,
    borderRadius: "999px",
    padding: "0.4rem 0.75rem",
    display: "inline-flex",
    alignItems: "center",
    fontWeight: "600",
    border: "1px solid var(--border-color, #ccc)",
  };
});

const multiWordPreviewStyle = computed(() => {
  if (!noteType.value) return {};
  const nt = noteType.value;
  const underlineColor = nt.defaultUnderline || nt.defaultColor || "#000000";

  return {
    display: "inline-block",
    fontWeight: "500",
    color: "var(--text-dark, #1a202c)",
    borderBottom: `3px solid ${underlineColor}`,
    paddingBottom: "1px",
    lineHeight: "1.2",
  };
});
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="emit('close')">
    <div class="note-type-card-modal-content">
      <div class="modal-header">
        <h3 class="card-title">Detalhes da Categoria</h3>
        <button
          @click="emit('close')"
          type="button"
          class="modal-close-btn"
          aria-label="Fechar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div v-if="noteType && !isLoadingData" class="card-content-grid">
        <div class="details-col">
          <h3 class="text-title-bold">
            {{ toTitleCase(noteType.noteTypeName) }}
            <span class="title-id">(Id: {{ noteType.noteTypeId }})</span>
          </h3>

          <div class="meta-grid">
            <div class="meta-item meta-item-full">
              <span class="meta-label">Cores Padrão</span>

              <span class="meta-value meta-value-colors">
                <div class="color-row">
                  <div
                    class="color-dot"
                    :style="{ backgroundColor: noteType.defaultColor }"
                    :title="noteType.defaultColor"
                  ></div>
                  <span class="color-label">Texto</span>
                </div>

                <div class="color-row">
                  <div
                    class="color-dot"
                    :style="{
                      backgroundColor:
                        noteType.defaultBackground === 'transparent'
                          ? '#ffffff'
                          : noteType.defaultBackground,
                    }"
                    :title="noteType.defaultBackground"
                  ></div>
                  <span class="color-label">Fundo</span>
                </div>

                <div
                  v-if="noteType.defaultRequiredUnderline"
                  class="color-row"
                >
                  <div
                    class="color-dot"
                    :style="{ backgroundColor: noteType.defaultUnderline }"
                    :title="noteType.defaultUnderline"
                  ></div>
                  <span class="color-label">Sublinhado</span>
                </div>
              </span>
            </div>
          </div>

          <hr class="content-separator" />

          <div class="previews-container">
            <div class="style-section-mini">
              <span class="meta-label">Palavra Única</span>
              <div class="preview-box">
                <div :style="singleWordPreviewStyle">Categoria</div>
              </div>
            </div>

            <div class="style-section-mini">
              <span class="meta-label">Várias Palavras</span>
              <div class="preview-box">
                <div
                  :style="[
                    transparentBackgroundStyle,
                    {
                      padding: '1rem 1.5rem',
                      borderRadius: '8px',
                      display: 'inline-flex',
                    },
                  ]"
                >
                  <span :style="multiWordPreviewStyle">
                    Trecho com várias palavras
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="footer-info">
            <div class="users-row">
              <div
                class="user-chip user-chip-clickable"
                @click="openUserModal(noteType.createdByUserId)"
              >
                <div
                  class="chip-avatar"
                  :style="{
                    backgroundImage: `url('${getCreatorAvatarUrl(noteType)}')`,
                  }"
                ></div>
                <div class="chip-text">
                  <span class="chip-label">Criador</span>
                  <span class="chip-name">{{
                    noteType.createdByUserName || "Não informado"
                  }}</span>
                  <span class="chip-date">
                    {{ formatDateTimeForDisplay(noteType.createdAt) }}
                  </span>
                </div>
              </div>

              <div
                class="user-chip user-chip-clickable"
                @click="openUserModal(noteType.updatedByUserId)"
              >
                <div
                  class="chip-avatar"
                  :style="{
                    backgroundImage: `url('${getUpdaterAvatarUrl(noteType)}')`,
                  }"
                ></div>
                <div class="chip-text">
                  <span class="chip-label">Atualizador</span>
                  <span class="chip-name">{{
                    noteType.updatedByUserName || "Não informado"
                  }}</span>
                  <span class="chip-date">
                    {{ formatDateTimeForDisplay(noteType.updatedAt) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="loading-state">
        <i class="fas fa-spinner fa-spin"></i> Carregando detalhes...
      </div>

      <UserCardModal
        v-if="showUserModal"
        :show="showUserModal"
        :userId="selectedUserId"
        @close="closeUserModal"
      />
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  width: 100%;
  height: 100%;
}

/* ✅ container harmonizado */
.note-type-card-modal-content {
  background: var(--surface);
  border-radius: 12px;
  width: 90%;
  max-width: 650px;
  position: relative;
  box-shadow: var(--shadow-modal);
  margin-bottom: 50px;
  overflow: hidden;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
}

/* ✅ body no padrão */
.card-content-grid {
  padding: 1rem;
}

.details-col {
  width: 100%;
}

/* ✅ título no “peso” do TextCardModal */
.text-title-bold {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0 0 12px 0;
  color: var(--text-dark);
  line-height: 1.25;
}

.title-id {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 4px;
}

.meta-grid {
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
}

/* cores padrão */
.meta-value-colors {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
}

.color-row {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.color-dot {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 4px;
  border: 1px solid var(--border-color);
  flex-shrink: 0;
}

.color-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-dark);
}

/* previews com espaçamento mais “tight” */
.previews-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.style-section-mini {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-box {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
}

/* footer */
.footer-info {
  padding-top: 8px;
  width: 100%;
}

/* ✅ chips lado a lado + wrap */
.users-row {
  display: flex;
  gap: 12px;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 8px;
}

</style>
